import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import crmService from "../../features/crm/service/crmService";
import {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
} from "../../features/crm/types/crmTypes";

// ============ CLIENTS ============
export const fetchClients = createAsyncThunk(
  "crm/fetchClients",
  async (filters, { rejectWithValue }) => {
    try {
      return await crmService.getClients(filters);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

export const fetchClientById = createAsyncThunk(
  "crm/fetchClientById",
  async (id, { rejectWithValue }) => {
    try {
      return await crmService.getClientById(id);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_ONE_FAILED);
    }
  },
);

export const createClient = createAsyncThunk(
  "crm/createClient",
  async (data, { rejectWithValue }) => {
    try {
      return await crmService.createClient(data);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.CREATE_FAILED);
    }
  },
);

export const updateClient = createAsyncThunk(
  "crm/updateClient",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await crmService.updateClient(id, data);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

export const deleteClient = createAsyncThunk(
  "crm/deleteClient",
  async (id, { rejectWithValue }) => {
    try {
      await crmService.deleteClient(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.DELETE_FAILED);
    }
  },
);

// ============ LEADS ============
export const fetchLeads = createAsyncThunk(
  "crm/fetchLeads",
  async (filters, { rejectWithValue }) => {
    try {
      return await crmService.getLeads(filters);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

export const fetchLeadById = createAsyncThunk(
  "crm/fetchLeadById",
  async (id, { rejectWithValue }) => {
    try {
      return await crmService.getLeadById(id);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_ONE_FAILED);
    }
  },
);

export const createLead = createAsyncThunk(
  "crm/createLead",
  async (data, { rejectWithValue }) => {
    try {
      return await crmService.createLead(data);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.CREATE_FAILED);
    }
  },
);

export const updateLead = createAsyncThunk(
  "crm/updateLead",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await crmService.updateLead(id, data);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

export const deleteLead = createAsyncThunk(
  "crm/deleteLead",
  async (id, { rejectWithValue }) => {
    try {
      await crmService.deleteLead(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.DELETE_FAILED);
    }
  },
);

export const convertLeadToClient = createAsyncThunk(
  "crm/convertLeadToClient",
  async ({ leadId, clientId }, { rejectWithValue }) => {
    try {
      return await crmService.convertLeadToClient(leadId, clientId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// ============ PROJECTS ============
export const fetchProjects = createAsyncThunk(
  "crm/fetchProjects",
  async (filters, { rejectWithValue }) => {
    try {
      return await crmService.getProjects(filters);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

export const fetchProjectById = createAsyncThunk(
  "crm/fetchProjectById",
  async (id, { rejectWithValue }) => {
    try {
      return await crmService.getProjectById(id);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_ONE_FAILED);
    }
  },
);

export const createProject = createAsyncThunk(
  "crm/createProject",
  async (data, { rejectWithValue }) => {
    try {
      return await crmService.createProject(data);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.CREATE_FAILED);
    }
  },
);

export const updateProject = createAsyncThunk(
  "crm/updateProject",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await crmService.updateProject(id, data);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

export const deleteProject = createAsyncThunk(
  "crm/deleteProject",
  async (id, { rejectWithValue }) => {
    try {
      await crmService.deleteProject(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.DELETE_FAILED);
    }
  },
);

// ============ TASKS ============
export const fetchTasks = createAsyncThunk(
  "crm/fetchTasks",
  async (_, { rejectWithValue }) => {
    try {
      return await crmService.getTasks();
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

export const createTask = createAsyncThunk(
  "crm/createTask",
  async (data, { rejectWithValue }) => {
    try {
      return await crmService.createTask(data);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.CREATE_FAILED);
    }
  },
);

export const updateTask = createAsyncThunk(
  "crm/updateTask",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await crmService.updateTask(id, data);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

export const completeTask = createAsyncThunk(
  "crm/completeTask",
  async (id, { rejectWithValue }) => {
    try {
      return await crmService.completeTask(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteTask = createAsyncThunk(
  "crm/deleteTask",
  async (id, { rejectWithValue }) => {
    try {
      await crmService.deleteTask(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.DELETE_FAILED);
    }
  },
);

// ============ INTERACTIONS ============
export const createInteraction = createAsyncThunk(
  "crm/createInteraction",
  async (data, { rejectWithValue }) => {
    try {
      return await crmService.createInteraction(data);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.CREATE_FAILED);
    }
  },
);

// ============ QUOTATIONS ============
export const createQuotation = createAsyncThunk(
  "crm/createQuotation",
  async (data, { rejectWithValue }) => {
    try {
      return await crmService.createQuotation(data);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.CREATE_FAILED);
    }
  },
);

export const updateQuotation = createAsyncThunk(
  "crm/updateQuotation",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await crmService.updateQuotation(id, data);
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

// ============ SLICE ============
const initialState = {
  // Clients
  clients: [],
  selectedClient: null,
  clientFilters: { status: "all", stage: "all", type: "all", search: "" },

  // Leads
  leads: [],
  selectedLead: null,
  leadFilters: {
    status: "all",
    priority: "all",
    temperature: "all",
    type: "all",
    search: "",
  },

  // Projects
  projects: [],
  selectedProject: null,
  projectFilters: { status: "all", stage: "all", type: "all", search: "" },

  // Tasks
  tasks: [],
  selectedTask: null,

  // Interactions
  interactions: [],

  // Quotations
  selectedQuotation: null,

  // Global
  loading: false,
  error: null,
  success: null,
};

const upsertInList = (state, listKey, updated) => {
  const idx = state[listKey].findIndex((i) => i.id === updated.id);
  if (idx !== -1) state[listKey][idx] = updated;
};

const crmSlice = createSlice({
  name: "crm",
  initialState,
  reducers: {
    clearErrorState: (state) => {
      state.error = null;
    },
    clearSuccessState: (state) => {
      state.success = null;
    },
    updateClientFilters: (state, action) => {
      state.clientFilters = { ...state.clientFilters, ...action.payload };
    },
    updateLeadFilters: (state, action) => {
      state.leadFilters = { ...state.leadFilters, ...action.payload };
    },
    updateProjectFilters: (state, action) => {
      state.projectFilters = { ...state.projectFilters, ...action.payload };
    },
    clearSelectedClient: (state) => {
      state.selectedClient = null;
    },
    clearSelectedLead: (state) => {
      state.selectedLead = null;
    },
    clearSelectedProject: (state) => {
      state.selectedProject = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Clients
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchClientById.fulfilled, (state, action) => {
        state.selectedClient = action.payload;
      })
      .addCase(createClient.fulfilled, (state, action) => {
        state.clients.unshift(action.payload);
        state.success = SUCCESS_MESSAGES.CLIENT_CREATE_SUCCESS;
      })
      .addCase(updateClient.fulfilled, (state, action) => {
        upsertInList(state, "clients", action.payload);
        state.success = SUCCESS_MESSAGES.CLIENT_UPDATE_SUCCESS;
      })
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.clients = state.clients.filter((c) => c.id !== action.payload);
        state.success = SUCCESS_MESSAGES.CLIENT_DELETE_SUCCESS;
      })

      // Leads
      .addCase(fetchLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload;
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchLeadById.fulfilled, (state, action) => {
        state.selectedLead = action.payload;
      })
      .addCase(createLead.fulfilled, (state, action) => {
        state.leads.unshift(action.payload);
        state.success = SUCCESS_MESSAGES.LEAD_CREATE_SUCCESS;
      })
      .addCase(updateLead.fulfilled, (state, action) => {
        upsertInList(state, "leads", action.payload);
        state.success = SUCCESS_MESSAGES.LEAD_UPDATE_SUCCESS;
      })
      .addCase(deleteLead.fulfilled, (state, action) => {
        state.leads = state.leads.filter((l) => l.id !== action.payload);
        state.success = SUCCESS_MESSAGES.LEAD_CONVERT_SUCCESS;
      })

      .addCase(convertLeadToClient.fulfilled, (state, action) => {
        state.success = SUCCESS_MESSAGES.LEAD_CONVERT_SUCCESS;
      })

      // Projects
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.selectedProject = action.payload;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.projects.unshift(action.payload);
        state.success = SUCCESS_MESSAGES.PROJECT_CREATE_SUCCESS;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        upsertInList(state, "projects", action.payload);
        state.success = SUCCESS_MESSAGES.PROJECT_UPDATE_SUCCESS;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.projects = state.projects.filter((p) => p.id !== action.payload);
        state.success = SUCCESS_MESSAGES.PROJECT_UPDATE_SUCCESS;
      })

      // Tasks
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.tasks = action.payload;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.tasks.unshift(action.payload);
        state.success = SUCCESS_MESSAGES.TASK_CREATE_SUCCESS;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        upsertInList(state, "tasks", action.payload);
        state.success = SUCCESS_MESSAGES.TASK_UPDATE_SUCCESS;
      })
      .addCase(completeTask.fulfilled, (state, action) => {
        upsertInList(state, "tasks", action.payload);
        state.success = SUCCESS_MESSAGES.TASK_COMPLETE_SUCCESS;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.tasks = state.tasks.filter((t) => t.id !== action.payload);
      })

      // Interactions
      .addCase(createInteraction.fulfilled, (state, action) => {
        state.interactions.unshift(action.payload);
        state.success = SUCCESS_MESSAGES.INTERACTION_CREATE_SUCCESS;
      })

      // Quotations
      .addCase(createQuotation.fulfilled, (state, action) => {
        state.selectedQuotation = action.payload;
      })
      .addCase(updateQuotation.fulfilled, (state, action) => {
        state.selectedQuotation = action.payload;
      });
  },
});

export const {
  clearErrorState,
  clearSuccessState,
  updateClientFilters,
  updateLeadFilters,
  updateProjectFilters,
  clearSelectedClient,
  clearSelectedLead,
  clearSelectedProject,
} = crmSlice.actions;

export default crmSlice.reducer;
